import React, { FC } from 'react';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import './SingleLineTextInput.css';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import FileCopy from '@material-ui/icons/FileCopyOutlined';
import { composeClasses } from '../../../libs/utils';

export type SingleLineTextInputTypes =
  | 'date'
  | 'text'
  | 'search'
  | 'range'
  | 'password'
  | 'number'
  | 'url';

export enum InputStyling {
  Minimal = 'bottom-border-only',
  Full = 'full-borders',
  Vanilla = 'vanilla-border'
}

export enum CursorStyling {
  Dropdown = 'dropdown-pointer',
  Input = 'input-pointer',
}

export enum InputDisplay {
  FullStretch = 'inp-block',
  Contained = 'inp-inline',
}

type Direction = 'upward' | 'downward';

export interface IOpttySingleLineTextInput
  extends React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  > {
  styling?: InputStyling;
  display?: InputDisplay;
  hasError?: boolean;
  errorText?: string;
  hasSuccess?: boolean;
  useAsDropDown?: boolean;
  dropDownDirection?: Direction;
  cursor?: CursorStyling;
  prefix?: string;
  suffix?: string;
  copy?: boolean;
  copyTextToClipboard?: (value: any) => void;
}

const SingleLineTextInput: FC<IOpttySingleLineTextInput> = ({
  tabIndex = 0,
  type = 'text',
  styling = InputStyling.Minimal,
  display = InputDisplay.FullStretch,
  hasError = false,
  errorText,
  hasSuccess = false,
  useAsDropDown = false,
  dropDownDirection = 'downward',
  cursor = CursorStyling.Input,
  prefix = '',
  suffix = '',
  copy = false,
  copyTextToClipboard,
  className,
  ...props
}) => {
  const inputType = type as SingleLineTextInputTypes;

  return (
    <div className="input-container">
      <div className={composeClasses('input-box', prefix || suffix ? 'input-box-with-affixes' : '')}>
        {prefix && <span className="input-prefix">{prefix}</span>}
        <input
          className={composeClasses(
            'input',
            styling,
            display,
            cursor,
            className,
            hasError ? 'has-error' : '',
            hasSuccess ? 'has-success' : '',
          )}
          tabIndex={tabIndex}
          type={inputType}
          {...props}
        />
        {suffix && <span className="input-suffix">{suffix}</span>}
        {hasSuccess && !hasError && (
          <span className="input-icon input-success-icon">
            <CheckCircleIcon />
          </span>
        )}
        {useAsDropDown && (
          <span className="input-icon input-dropdown-icon">
            {dropDownDirection === 'upward' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
          </span>
        )}
        {copy && copyTextToClipboard && (
          <span
            className="input-icon input-copy-icon"
            onClick={() => copyTextToClipboard(props.value)}
            onKeyDown={
              (e) => { if (e.key === 'Enter' || e.key === ' ') copyTextToClipboard(props.value); }
            }
            role="button"
            tabIndex={0}
          >
            <FileCopy />
          </span>
        )}
      </div>
      {hasError && errorText && (
        <span className="input-error-text">{errorText}</span>
      )}
    </div>
  );
};

export default SingleLineTextInput;